import { Injectable } from '@angular/core';
import { registerPlugin } from '@capacitor/core';

interface AndroidBillingPlugin {
  loadProducts(options: { products: Array<{ id: string; type: string }> }): Promise<{ products: any[] }>;
  purchase(options: { id: string; type: string }): Promise<{ purchase: any }>;
  restorePurchases(): Promise<{ purchases: any[] }>;
  finish(options: { purchaseToken: string; consumable?: boolean }): Promise<void>;
}

const AndroidBilling = registerPlugin<AndroidBillingPlugin>('AndroidPlayBilling');

type StoreEvent = 'updated' | 'cancelled' | 'approved' | 'owned' | 'error';

@Injectable({ providedIn: 'root' })
export class AndroidPlayBillingStoreService {
  readonly PAID_SUBSCRIPTION = 'paid subscription';
  readonly NON_RENEWING_SUBSCRIPTION = 'non renewing subscription';
  readonly FREE_SUBSCRIPTION = 'free subscription';
  readonly CONSUMABLE = 'consumable';
  readonly NON_CONSUMABLE = 'non consumable';
  readonly OWNED = 'owned';
  readonly VALID = 'valid';
  readonly INVALID = 'invalid';
  readonly REGISTERED = 'registered';
  readonly APPROVED = 'approved';
  readonly FINISHED = 'finished';
  readonly DEBUG = 4;
  readonly INFO = 3;
  readonly WARNING = 2;
  readonly ERROR = 1;
  readonly QUIET = 0;

  verbosity = this.WARNING;
  validator: any;
  applicationUsername: any;
  autoFinishTransactions = false;
  sandbox = false;
  disableHostedContent = false;

  private registrations = new Map<string, any>();
  private productMap = new Map<string, any>();
  private handlers = new Map<string, Map<StoreEvent, Function[]>>();
  private errorHandlers: Function[] = [];
  private readyHandlers: Function[] = [];
  private loadingPromise: Promise<void> | null = null;

  get products(): any[] {
    return Array.from(this.productMap.values());
  }

  get(idOrAlias: string): any {
    return this.productMap.get(idOrAlias) || this.productMap.get(this.registrationIdForAlias(idOrAlias));
  }

  register(productOrProducts: any | any[]): void {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];
    products.filter(Boolean).forEach((product) => {
      this.registrations.set(product.id, { ...product });
      if (product.alias) {
        this.registrations.set(product.alias, { ...product });
      }
      if (!this.productMap.has(product.id)) {
        this.productMap.set(product.id, this.makeProduct({
          id: product.id,
          alias: product.alias || product.id,
          type: product.type,
          state: this.REGISTERED,
          valid: false,
          owned: false,
        }));
      }
    });
  }

  when(query: string | any): any {
    const id = typeof query === 'string' ? query : query?.id;
    const chain: any = {};
    (['updated', 'cancelled', 'approved', 'owned', 'error'] as StoreEvent[]).forEach((event) => {
      chain[event] = (callback: Function) => {
        this.addHandler(id, event, callback);
        return chain;
      };
    });
    return chain;
  }

  once(query: string | any): any {
    const id = typeof query === 'string' ? query : query?.id;
    const chain: any = {};
    (['updated', 'cancelled', 'approved', 'owned', 'error'] as StoreEvent[]).forEach((event) => {
      chain[event] = (callback: Function) => {
        const once = (...args: any[]) => {
          this.off(once);
          callback(...args);
        };
        this.addHandler(id, event, once);
        return chain;
      };
    });
    return chain;
  }

  ready(callback: Function): void {
    this.readyHandlers.push(callback);
    if (this.products.some((product) => product.valid)) {
      callback();
    }
  }

  error(callback: Function): void {
    this.errorHandlers.push(callback);
  }

  off(callback: Function): void {
    this.errorHandlers = this.errorHandlers.filter((handler) => handler !== callback);
    this.readyHandlers = this.readyHandlers.filter((handler) => handler !== callback);
    this.handlers.forEach((events) => events.forEach((callbacks, event) => {
      events.set(event, callbacks.filter((handler) => handler !== callback));
    }));
  }

  refresh(): Promise<void> {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }
    const unique = Array.from(new Map(
      Array.from(this.registrations.values()).map((product) => [product.id, product])
    ).values());
    this.loadingPromise = AndroidBilling.loadProducts({
      products: unique.map((product) => ({ id: product.id, type: this.nativeType(product.type) })),
    }).then(async ({ products }) => {
      products.forEach((details) => this.updateProduct(details));
      const restored = await AndroidBilling.restorePurchases();
      restored.purchases.forEach((purchase) => this.applyPurchase(purchase, false));
      this.readyHandlers.forEach((handler) => handler());
    }).catch((error) => this.emitError(error)).finally(() => {
      this.loadingPromise = null;
    });
    return this.loadingPromise;
  }

  update(): Promise<void> {
    return this.refresh();
  }

  async order(productOrId: any): Promise<any> {
    const id = typeof productOrId === 'string' ? productOrId : productOrId?.id;
    const registration = this.registrations.get(id);
    if (!registration) {
      const error = { code: 'PRODUCT_NOT_REGISTERED', message: `Product ${id} is not registered` };
      this.emit(id, 'error', error);
      this.emitError(error);
      return error;
    }
    try {
      const { purchase } = await AndroidBilling.purchase({ id: registration.id, type: this.nativeType(registration.type) });
      this.applyPurchase(purchase, true);
      return undefined;
    } catch (error: any) {
      if (error?.code === 'USER_CANCELED' || error?.message?.includes('cancel')) {
        this.emit(id, 'cancelled', this.get(id));
      } else {
        this.emit(id, 'error', error);
        this.emitError(error);
      }
      return error;
    }
  }

  manageSubscriptions(): void {
    window.open('https://play.google.com/store/account/subscriptions', '_system');
  }

  manageBilling(): void {
    window.open('https://play.google.com/store/account', '_system');
  }

  getApplicationUsername(): string {
    return typeof this.applicationUsername === 'function' ? this.applicationUsername() : this.applicationUsername;
  }

  private updateProduct(details: any): any {
    const registration = this.registrations.get(details.id) || {};
    const current = this.productMap.get(details.id) || {};
    const product = this.makeProduct({
      ...current,
      ...details,
      alias: registration.alias || details.id,
      type: registration.type,
      state: current.owned ? this.OWNED : this.VALID,
      valid: true,
      owned: !!current.owned,
      canPurchase: !current.owned,
    });
    this.productMap.set(details.id, product);
    if (product.alias) this.productMap.set(product.alias, product);
    this.emit(details.id, 'updated', product);
    return product;
  }

  private applyPurchase(purchase: any, newlyApproved: boolean): void {
    (purchase.products || []).forEach((id: string) => {
      const existing = this.get(id) || { id };
      const product = this.makeProduct({
        ...existing,
        owned: purchase.state === 'PURCHASED',
        state: purchase.state === 'PURCHASED' ? this.OWNED : purchase.state,
        transaction: purchase,
        purchaseToken: purchase.purchaseToken,
        canPurchase: false,
      });
      this.productMap.set(id, product);
      if (product.alias) this.productMap.set(product.alias, product);
      if (newlyApproved && product.owned) this.emit(id, 'approved', product);
      if (product.owned) this.emit(id, 'owned', product);
      this.emit(id, 'updated', product);
    });
  }

  private makeProduct(data: any): any {
    return {
      ...data,
      finish: () => data.purchaseToken
        ? AndroidBilling.finish({
            purchaseToken: data.purchaseToken,
            consumable: data.type === this.CONSUMABLE,
          }).then(() => { data.state = this.FINISHED; })
        : Promise.resolve(),
    };
  }

  private nativeType(type: string): string {
    return type === this.PAID_SUBSCRIPTION || type === this.NON_RENEWING_SUBSCRIPTION
      ? 'subs'
      : 'inapp';
  }

  private registrationIdForAlias(alias: string): string {
    return this.registrations.get(alias)?.id || alias;
  }

  private addHandler(id: string, event: StoreEvent, callback: Function): void {
    const productId = this.registrationIdForAlias(id);
    if (!this.handlers.has(productId)) this.handlers.set(productId, new Map());
    const events = this.handlers.get(productId)!;
    events.set(event, [...(events.get(event) || []), callback]);
  }

  private emit(id: string, event: StoreEvent, payload: any): void {
    (this.handlers.get(this.registrationIdForAlias(id))?.get(event) || []).forEach((handler) => handler(payload));
  }

  private emitError(error: any): void {
    this.errorHandlers.forEach((handler) => handler(error));
  }
}
